import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  Box,
  Typography,
  Container,
  Stack,
  CircularProgress,
  Button,
  Skeleton,
  Tab,
  Tabs,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import {
  useGetProjectsQuery,
  useGetCategoriesQuery,
} from "../../../Store/api/rtk";
import SeoHelmet from "../../../Components/SeoHelmet";

import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { projectNavigatePath } from "../../../Utils/detailsNavigatePath";

gsap.registerPlugin(ScrollTrigger);

export default function ProjectsList() {
  const theme = useTheme();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [allProjects, setAllProjects] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");

  const countPerPage = 10;

  const { data: categoriesData } = useGetCategoriesQuery({ page: 1 });
  const { data, isLoading, isFetching, isError, refetch } = useGetProjectsQuery(
    {
      count: countPerPage,
      page: page,
      category: selectedCategory === "all" ? undefined : selectedCategory,
    },
  );

  const isFetchingRef = useRef(isFetching);
  isFetchingRef.current = isFetching;

  // handle tab change
  const handleCategoryChange = (event, newValue) => {
    if (newValue === selectedCategory) return;
    setSelectedCategory(newValue);
    setPage(1);
    setAllProjects([]);
    setHasMore(true);
  };

  // projects update
  useEffect(() => {
    if (data?.data) {
      const newProjects = data.data.d || [];
      const totalPages = data.data.num_of_pages || 1;

      if (page === 1) {
        setAllProjects(newProjects);
      } else {
        setAllProjects((prev) => {
          const existingCodes = new Set(prev.map((p) => p.code));
          const uniqueNew = newProjects.filter(
            (p) => !existingCodes.has(p.code),
          );
          return [...prev, ...uniqueNew];
        });
      }

      if (page >= totalPages) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    }
  }, [data, page]);

  // cards animation
  useEffect(() => {
    const newCards = gsap.utils.toArray(".project-card.not-animated");

    newCards.forEach((card) => {
      card.classList.remove("not-animated");

      gsap.fromTo(
        card,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: "power2.out",
          scrollTrigger: {
            trigger: card,
            start: "top 85%",
            toggleActions: "play none none none",
          },
        },
      );
    });

    setTimeout(() => {
      ScrollTrigger.refresh();
    }, 100);
  }, [allProjects]);

  // scroll
  useEffect(() => {
    let trigger;

    if (hasMore && !isError) {
      trigger = ScrollTrigger.create({
        trigger: ".bottom-loader-trigger",
        start: "top 95%",
        onEnter: () => {
          if (!isFetchingRef.current) {
            setPage((prev) => prev + 1);
          }
        },
      });
    }

    return () => {
      if (trigger) trigger.kill();
    };
  }, [hasMore, isError, allProjects]);

  // clearing
  useEffect(() => {
    return () => {
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, []);

  // tabs
  const categories = categoriesData?.data?.d || [];
  const tabs = [
    { code: "all", title: "همه پروژه‌ها" },
    ...categories.map((cat) => {
      return { code: cat?.code, title: cat?.title };
    }),
  ];

  const tabItems = tabs.map((tab) => (
    <Tab key={tab.code} value={tab.code} label={tab.title} />
  ));

  const allProjectsItem = allProjects.map((project, index) => (
    <Grid
      className="project-card not-animated"
      key={`${project.code}-${index}`}
      size={{ xs: 12, lg: 6, xl: 4 }}
      sx={{ opacity: 0 }}
    >
      <Box
        onClick={() => navigate(projectNavigatePath(project))}
        sx={{
          position: "relative",
          height: 350,
          borderRadius: 3,
          overflow: "hidden",
          cursor: "pointer",
          backgroundImage: `url(${import.meta.env.VITE_URL + (project.image_path?.[0] || "")})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          transition: "transform 0.3s ease",
          "&:hover": {
            transform: "scale(1.02)",
            "& .overlay": { opacity: 1 },
          },
        }}
      >
        <Box
          className="overlay"
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.75)",
            opacity: 0,
            transition: "opacity 0.3s ease",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            p: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: "#fff",
              fontWeight: 700,
              mb: 1,
              transform: "translateY(0)",
              transition: "transform 0.3s ease",
              ".overlay:hover &": { transform: "translateY(-5px)" },
            }}
          >
            {project.name}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.primary.main,
              fontWeight: 600,
              fontSize: "0.9rem",
              letterSpacing: "1px",
              textTransform: "uppercase",
            }}
          >
            {project?.category?.title || project?.category || ""}
          </Typography>
        </Box>
      </Box>
    </Grid>
  ));

  return (
    <>
      <SeoHelmet
        title="پروژه‌های ویرا | نمونه کارهای طراحی وب و توسعه نرم‌افزار"
        metaDescription="مشاهده نمونه کارها و پروژه‌های ویرا در زمینه طراحی سایت، توسعه نرم‌افزار، طراحی رابط کاربری و هویت بصری."
        metaKeywords="نمونه کار طراحی وب, پروژه‌های ویرا, پورتفولیو طراحی سایت, هویت بصری"
        scheme={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "@id": "https://vira-tec.ir/projects-list#webpage",
          url: "https://vira-tec.ir/projects-list",
          name: "نمونه کارهای ویرا | پروژه‌های طراحی سایت و توسعه نرم‌افزار",
          description:
            "مشاهده نمونه کارها و پروژه‌های ویرا در زمینه طراحی سایت، توسعه نرم‌افزار، طراحی رابط کاربری و هویت بصری.",
          inLanguage: "fa-IR",
          isPartOf: {
            "@type": "WebSite",
            "@id": "https://vira-tec.ir/#website",
            url: "https://vira-tec.ir/",
            name: "ویرا تک آروند آریا",
          },
          mainEntity: {
            "@type": "ItemList",
            name: "نمونه کارهای ویرا",
            numberOfItems: 26,
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "نیموبایل",
                url: "https://vira-tec.ir/projects/aa2d9ae3457422e",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "کاوش اندیشه رستگار",
                url: "https://vira-tec.ir/projects/a445feb9db6ae52",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: "آجیل عمده نیکنام",
                url: "https://vira-tec.ir/projects/d332f26ce924de0",
              },
              {
                "@type": "ListItem",
                position: 4,
                name: "هوریسا حجاب",
                url: "https://vira-tec.ir/projects/d0f287faba72f87",
              },
              {
                "@type": "ListItem",
                position: 5,
                name: "میللند (mealland)",
                url: "https://vira-tec.ir/projects/dbde9a01b1b65ae",
              },
              {
                "@type": "ListItem",
                position: 6,
                name: "رابی",
                url: "https://vira-tec.ir/projects/2b3e9aa16fdf17e",
              },
              {
                "@type": "ListItem",
                position: 7,
                name: "توزیع نمونه سازمان استاندارد",
                url: "https://vira-tec.ir/projects/f7700b0fd655956",
              },
              {
                "@type": "ListItem",
                position: 8,
                name: "ویوان",
                url: "https://vira-tec.ir/projects/d36ccffac736544",
              },
              {
                "@type": "ListItem",
                position: 9,
                name: "کنته",
                url: "https://vira-tec.ir/projects/d1b29b56ce195ee",
              },
              {
                "@type": "ListItem",
                position: 10,
                name: "آجیل نیکنام",
                url: "https://vira-tec.ir/projects/5e6168dde263ae6",
              },
              {
                "@type": "ListItem",
                position: 11,
                name: "سیگلوی",
                url: "https://vira-tec.ir/projects/40b6e232318d1be",
              },
              {
                "@type": "ListItem",
                position: 12,
                name: "رستوران آرامش",
                url: "https://vira-tec.ir/projects/c5a798cdc1475a3",
              },
              {
                "@type": "ListItem",
                position: 13,
                name: "املاک صادقیان",
                url: "https://vira-tec.ir/projects/1f0b977307cf7e2",
              },
              {
                "@type": "ListItem",
                position: 14,
                name: "اپلیکیشن رستوران لوکا",
                url: "https://vira-tec.ir/projects/6d9049a09c782a3",
              },
              {
                "@type": "ListItem",
                position: 15,
                name: "توزیع برق",
                url: "https://vira-tec.ir/projects/7f746f3c6739c84",
              },
              {
                "@type": "ListItem",
                position: 16,
                name: "اتحادیه طرقبه شاندیز",
                url: "https://vira-tec.ir/projects/be3125c8ad860aa",
              },
              {
                "@type": "ListItem",
                position: 17,
                name: "رستوران فردیس",
                url: "https://vira-tec.ir/projects/098a16f8be0cc5e",
              },
              {
                "@type": "ListItem",
                position: 18,
                name: "گروه مهندسی لاین",
                url: "https://vira-tec.ir/projects/535dfb9eff90560",
              },
              {
                "@type": "ListItem",
                position: 19,
                name: "طراحی فرش نقش و نگار",
                url: "https://vira-tec.ir/projects/59d48927f3a2e3a",
              },
              {
                "@type": "ListItem",
                position: 20,
                name: "صندوق فروشگاهی",
                url: "https://vira-tec.ir/projects/97c1b21fa91ad3f",
              },
              {
                "@type": "ListItem",
                position: 21,
                name: "ماهرخسار",

                url: "https://vira-tec.ir/projects/4d41299ba5ef2df",
              },
              {
                "@type": "ListItem",
                position: 22,
                name: "یاس لوگو",
                url: "https://vira-tec.ir/projects/0c52148bdd49b2d",
              },
              {
                "@type": "ListItem",
                position: 23,
                name: "سیگلوی لوگو",
                url: "https://vira-tec.ir/projects/aa6dfcdaf5eec82",
              },
              {
                "@type": "ListItem",
                position: 24,
                name: "لوگوی مللند",
                url: "https://vira-tec.ir/projects/c29666e511a676b",
              },
              {
                "@type": "ListItem",
                position: 25,
                name: "طراحی لوگو ویوان",
                url: "https://vira-tec.ir/projects/f775be7df3eb2c2",
              },
              {
                "@type": "ListItem",
                position: 26,
                name: "خلق جلوه بصری پارمیس",
                url: "https://vira-tec.ir/projects/ddc32305c0656fe",
              },
            ],
          },
        }}
      />

      <Box
        sx={{
          py: 15,
          minHeight: "100vh",
          "& *": { direction: "rtl", boxSizing: "border-box" },
        }}
      >
        <Container maxWidth="xxl">
          {/* Header Section */}
          <Stack
            direction="column"
            alignItems="center"
            justifyContent="center"
            sx={{ mb: 4, textAlign: "center" }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "1.5rem", lg: "2rem", xxl: "2.5rem" },
                fontWeight: 800,
                mb: 1.5,
                color: "secondary.main",
              }}
            >
              پروژه‌های ویرا
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.secondary,
                maxWidth: 650,
                fontWeight: 400,
                lineHeight: 1.8,
              }}
            >
              مجموعه‌ای از تجربیات موفق در طراحی وبسایت‌ها، پلتفرم‌ها و هویت‌های
              بصری ماندگار برای مشتریانمان
            </Typography>
            <Box
              sx={{
                width: 80,
                height: 3,
                bgcolor: theme.palette.secondary.main,
                borderRadius: 2,
                mt: 3,
              }}
            />
          </Stack>

          {/* Tabs Section */}
          <Box
            sx={{
              mb: 6,
              display: "flex",
              justifyContent: "center",
              overflowX: "auto",
              py: 1,
            }}
          >
            <Tabs
              value={selectedCategory}
              onChange={handleCategoryChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                minHeight: "unset",

                bgcolor: "background.paper",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: "999px",
                p: 1,

                backdropFilter: "blur(10px)",

                "& .MuiTabs-flexContainer": {
                  gap: 1,
                },

                "& .MuiTab-root": {
                  minHeight: "unset",
                  minWidth: "auto",

                  px: 3,
                  py: 1.3,

                  fontSize: {
                    xs: "0.85rem",
                    sm: "0.95rem",
                  },

                  fontWeight: 700,
                  color: theme.palette.text.secondary,

                  borderRadius: "999px",

                  transition: "all .3s ease",

                  "&:hover": {
                    bgcolor: theme.palette.action.hover,
                    color: theme.palette.text.primary,
                  },

                  "&.Mui-selected": {
                    color: "#fff",
                    background: `linear-gradient(135deg,
            ${theme.palette.secondary.main},
            ${theme.palette.secondary.dark}
          )`,
                    boxShadow: `0 10px 25px ${theme.palette.secondary.main}40`,
                  },
                },

                "& .MuiTabs-indicator": {
                  display: "none",
                },

                "& .MuiTabs-scrollButtons": {
                  color: theme.palette.secondary.main,
                  borderRadius: "50%",
                  rotate: "180deg",
                },
              }}
            >
              {tabItems}
            </Tabs>
          </Box>

          {/* loading*/}
          {isLoading && page === 1 ? (
            <Grid container spacing={3}>
              {[1, 2, 3, 4, 5, 6].map((item) => (
                <Grid key={item} size={{ xs: 12, lg: 6, xl: 4 }}>
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={350}
                    sx={{
                      borderRadius: 3,
                      bgcolor: theme.palette.action.hover,
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          ) : allProjects.length === 0 && !isFetching ? (
            /*with out project*/
            <Stack alignItems="center" sx={{ py: 8 }}>
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.text.secondary,
                  display: isLoading ? "none" : "flex",
                }}
              >
                هیچ پروژه‌ای در این دسته‌بندی یافت نشد.
              </Typography>
            </Stack>
          ) : (
            /* projects */
            <Grid container spacing={3}>
              {allProjectsItem}
            </Grid>
          )}

          {/* scroll trigger */}
          <Box
            className="bottom-loader-trigger"
            sx={{ height: 20, width: "100%", mt: 4 }}
          />

          {/* loading*/}
          {isFetching && page > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <CircularProgress color="primary" />
            </Box>
          )}

          {/*error */}
          {isError && page > 1 && (
            <Stack direction="column" alignItems="center" sx={{ mt: 4 }}>
              <Typography color="error" sx={{ mb: 2 }}>
                خطا در دریافت اطلاعات سرور.
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => refetch()}
              >
                تلاش مجدد
              </Button>
            </Stack>
          )}
        </Container>
      </Box>
    </>
  );
}
